import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Mui
import { TreeView, TreeItem } from "@mui/lab";
import { Box, useTheme } from "@mui/material";

// Components
import { ItemLabel } from "./components/ItemLabel";

// Styles
import mainMenuStyles from "./MainMenu.styles";

// Config
import { menuConfig, MenuItem } from "./mainMenu.config";

// Carousel
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import { useAppSelector } from "app/hooks";
import { getReportStatus } from "store/slice/Pace";
import { getBriefs } from "store/slice/Project/briefs/briefsSlice";
import { getInsights } from "store/slice/Project/insights/insightsSlice";
import { getProject } from "store/slice/Project/projectData/ProjectSlice";
import { getSearchAis } from "store/slice/Project/searchAis/searchAisSlice";
import { useAbility } from "@casl/react";
import { AbilityContext, TAppAbility, compoundPermissions } from "context/Ability";
import { useAuth } from "utils";

const markAsSelected = (item: MenuItem, pathname: string) => {
    const itemFullPath = `${item.base}${item.path}`;

    const isFullPathInPathname = pathname.includes(itemFullPath);
    const isExtraPathInPathname = item?.extraPaths?.some((path) => pathname.includes(path)) || false;

    let newItem = item;

    if (item?.children?.length) {
        const children = item.children.map((menuItem) => {
            return menuItem.map((children) => {
                const childFullPath = `${children.base}${children.path}`;
                return {
                    ...children,
                    isSelected: pathname.includes(childFullPath),
                };
            });
        });
        newItem = { ...item, children };
    }

    return { ...newItem, isSelected: isFullPathInPathname || isExtraPathInPathname };
};

const filterSideItemPermissions = (ability: TAppAbility, menuItems: MenuItem[]): MenuItem[] => {
    return menuItems
        .filter(({ permissions }) => compoundPermissions(permissions, ability))
        ?.map((menuItem) => {
            if (menuItem.children) {
                return {
                    ...menuItem,
                };
            }
            return menuItem;
        });
};

const flattenMenu = (menu: MenuItem[]): MenuItem[] => {
    return menu.flatMap((item) => {
        const nestedChildren = item.children?.flatMap((group) => flattenMenu(group)) ?? [];
        return [item, ...nestedChildren];
    });
};

const updateExpandedItems = (menuItems: MenuItem[], nodeIds: string[]): MenuItem[] => {
    return menuItems.map((item) => {
        const children = item.children?.map((group) => updateExpandedItems(group, nodeIds));

        return {
            ...item,
            children,
            isExpanded: item.shouldExpand ? nodeIds.includes(item.id) : item.isExpanded,
        };
    });
};

const MainMenu = () => {
    const navigate = useNavigate();
    const classes = mainMenuStyles();
    const theme = useTheme();
    const ability = useAbility(AbilityContext);
    const { sessionData } = useAuth();

    const { pathname, search } = useLocation();

    const canSeePaceMenu =
        ability.can("see", "SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON") ||
        ability.can("see", "SYS_ADMIN_VIEW_PACE_PORTAL");

    const reportStatus = useAppSelector(getReportStatus);

    const projectData = useAppSelector(getProject).data;
    const projectInsights = useAppSelector(getInsights).data;
    const projectBriefs = useAppSelector(getBriefs).data;
    const projectSearchAIs = useAppSelector(getSearchAis).data;

    const [menu, setMenu] = useState(filterSideItemPermissions(ability, menuConfig));
    const [currentSlide, setCurrentSlide] = useState(0);
    const hasUpdatedPathOnLoad = useRef(false);

    const flatMenu = flattenMenu(menu);
    const expandedItems = flatMenu.filter((item) => item.isExpanded);
    const selectedItems = flatMenu.filter((item) => item.isSelected);

    const handleTreeViewToggle = (event: React.ChangeEvent<unknown>, nodeIds: string[]) => {
        setMenu((currentMenu) => {
            return updateExpandedItems(currentMenu, nodeIds);
        });
    };

    const handleSetCurrentSlide = (slide: number) => {
        setCurrentSlide(slide);
    };

    const handleNavigate = (item: MenuItem) => {
        if (item.shouldExpand || item.url === undefined) return;

        // Basic Page
        navigate(item.url);
    };

    // Update current slide on first load
    useEffect(() => {
        if (selectedItems.length && !hasUpdatedPathOnLoad.current) {
            hasUpdatedPathOnLoad.current = true;
            setCurrentSlide((currentSlide) => selectedItems.at(0)?.slide || currentSlide);
        }
    }, [selectedItems]);

    // Mark item as selected according to URL
    useEffect(() => {
        setMenu((currentMenu) => {
            return currentMenu.map((item) => {
                // Update item
                if (item?.updatePathAndItems) {
                    return item.updatePathAndItems(item, `${pathname}${search}`, {
                        sessionData: sessionData,
                        data: projectData,
                        insights: projectInsights,
                        briefs: projectBriefs,
                        searchAIs: projectSearchAIs,
                        reportStatus,
                    });
                }

                // Others
                return markAsSelected(item, `${pathname}${search}`);
            });
        });
    }, [pathname, search, projectBriefs, projectData, projectInsights, projectSearchAIs, reportStatus, sessionData]);

    const renderNodes = (options: MenuItem[], parent?: MenuItem) => {
        return options.map((item) => {
            return (
                <TreeItem
                    key={item.id}
                    nodeId={item.id}
                    label={
                        <ItemLabel
                            isSubItem={item.parentId !== undefined}
                            item={item}
                            handleNavigate={handleNavigate}
                            setSlide={handleSetCurrentSlide}
                            sx={{
                                padding: theme.spacing(1, 1.2, 1, item.indent ?? 1),
                                minHeight: parent?.goToSlide !== undefined ? 32 : 40,
                            }}
                        />
                    }
                    className={cn({
                        [classes.indentedItem]: item.indent,
                        [classes.changeItem]: item.goToSlide !== undefined,
                        [classes.disabledItem]: item.disabled,
                        [classes.topSeparator]: item.topSeparator,
                        [classes.bottomSeparator]: item.bottomSeparator,
                        [classes.indentedItem]: Boolean(parent?.indent),
                    })}
                    sx={{
                        mb: item.verticalSpace,
                    }}
                >
                    {item?.children?.map((children, index) => {
                        const isInsight = children.some((child) => child.id === "ld-insights");
                        return (
                            <ul className={cn({ [classes.nestedParent]: isInsight })} key={index}>
                                {renderNodes(children, item)}
                            </ul>
                        );
                    })}
                </TreeItem>
            );
        });
    };

    const MenuSlide = ({ options }: { options: MenuItem[] }) => {
        return <ul style={{ listStyleType: "none", padding: 0 }}>{renderNodes(options)}</ul>;
    };

    return (
        <TreeView
            aria-label="Main Menu"
            className={classes.mainMenu}
            expanded={expandedItems.map((item) => item.id)}
            selected={selectedItems.map((item) => item.id)}
            onNodeToggle={handleTreeViewToggle}
        >
            <Carousel
                interval={5000}
                showArrows={false}
                showThumbs={false}
                showStatus={false}
                showIndicators={false}
                selectedItem={currentSlide}
            >
                <Box>
                    <MenuSlide options={menu.filter((item) => item.slide === 0)} />
                </Box>
                <Box>
                    <MenuSlide options={menu.filter((item) => item.slide === 1)} />
                </Box>
                {canSeePaceMenu ? (
                    <Box>
                        <MenuSlide options={menu.filter((item) => item.slide === 2)} />
                    </Box>
                ) : (
                    <></>
                )}
            </Carousel>
        </TreeView>
    );
};

export default MainMenu;
